import { lazy } from 'react';
import { createBrowserRouter, defer, redirect } from 'react-router-dom';
import { toast } from 'react-toastify';

import LazyRouteElement from '@/router/LazyRouteElement';
import i18n from '@/config/i18n';

import {
    checkMallSession,
    checkRefreshToken,
    checkSwitchSession,
    checkUserSession,
    clearTemporaryToken,
    getMallUserIdByToken,
    getOwnMalls,
    getTemporaryToken,
    getUserData,
    refreshToken,
    setTemporaryToken,
    signOutSession,
    socialLogin,
} from '@/services/auth.service';
import {
    checkMallPermission,
    getBusinessInfo,
    getCheckIsReturnIssue,
    getMallInfo,
    getNewInquiryCount,
    getNoticeList,
    getSalesPolicy,
    getShippingPolicyList,
} from '@/services/mall.service';
import { getProduct, getProductList, GetProductParams } from '@/services/products.service';
import sessionStore from '@/store/modules/sessionStore';
import spaceUserV2Store from '@/store/modules/spaceUserV2Store';

import qs from 'qs';
import { isSodomallPartnerDomain } from '@/utils/checkDomain';

// layouts
import DDMLayout from '@/externals/ddm/layouts/DDMLayout';
import ManagerLayout from '@/layouts/ManagerLayout';
import MobilePageLayout from '@/layouts/MobilePages';

// pages
import BusinessInfo from '@/pages/mall/policy/BusinessInfo';
import Register from '@/pages/Register';
import RootCatalog from '@/pages/RootCatalog';
import Login from '@/pages/Login';

import Error from '@/pages/Errors';

// lazy load page components
const LazyAbout = lazy(() => import('@/pages/About').then((module) => module));

const LazySignUp = lazy(() => import('@/pages/Register/SignUp').then((module) => module));
const LazyRegisterTerms = lazy(() => import('@/pages/Register/Terms').then((module) => module));
const LazyRegisterForm = lazy(() => import('@/pages/Register/RegisterForm').then((module) => module));
const LazyRegisterResult = lazy(() => import('@/pages/Register/RegisterResult').then((module) => module));

const LazyToastEditor = lazy(() => import('@/pages/TemplateDemo/ToastEditor').then((module) => module));
const LazySalesPolicy = lazy(() => import('@/pages/mall/policy/SalesPolicy').then((module) => module));
const LazyShippingPolicy = lazy(() => import('@/pages/mall/policy/ShippingPolicy').then((module) => module));

const LazyProducts = lazy(() => import('@/pages/mall/Products').then((module) => module));
const LazyProductEditor = lazy(() => import('@/pages/mall/ProductEditor').then((module) => module));
const LazyGroupSaleProducts = lazy(() => import('@/pages/mall/GroupSalesProducts').then((module) => module));
const LazyGroupSaleProductsSodomall = lazy(() =>
    import('@/pages/mall/GroupSalesProductsSodomall').then((module) => module),
);
const LazyCreateContractSodomall = lazy(() => import('@/pages/mall/CreateContractSodomall').then((module) => module));
const LazyOrderGroups = lazy(() => import('@/pages/mall/OrderGroups').then((module) => module));
const LazyShippingManagements = lazy(() => import('@/pages/mall/ShippingManagements').then((module) => module));
const LazyReturnOrders = lazy(() => import('@/pages/mall/ReturnOrders').then((module) => module));
const LazyIssueOrders = lazy(() => import('@/pages/mall/IssueOrders').then((module) => module));
const LazyRefundOrders = lazy(() => import('@/pages/mall/RefundOrders').then((module) => module));

const LazyGroupSalesOrders = lazy(() => import('@/pages/mall/GroupSalesOrders').then((module) => module));
const LazyGroupSalesOrdersSodomall = lazy(() =>
    import('@/pages/mall/GroupSalesOrdersSodomall').then((module) => module),
);

const LazyTagManagement = lazy(() => import('@/pages/TagManager').then((module) => module));

const LazyCustomers = lazy(() => import('@/pages/mall/Customers').then((module) => module));
const LazyInquiries = lazy(() => import('@/pages/mall/Inquiries').then((module) => module));
const LazyNotices = lazy(() => import('@/pages/mall/Notices').then((module) => module));

const LazyTemplateDemo = lazy(() => import('@/pages/TemplateDemo').then((module) => module));
const LazyButtonPage = lazy(() => import('@/pages/TemplateDemo/Button').then((module) => module));
const LazyDialogPage = lazy(() => import('@/pages/TemplateDemo/Dialog').then((module) => module));
const LazyCustomFieldsPage = lazy(() => import('@/pages/TemplateDemo/CustomFields').then((module) => module));
const LazyBannerDemo = lazy(() => import('@/pages/TemplateDemo/BannerDemo').then((module) => module));

const LazySalesList = lazy(() => import('@/pages/mall/Statement/SalesList').then((module) => module));
const LazySalesListSimple = lazy(() => import('@/pages/mall/Statement/SalesListSodomall').then((module) => module));
const LazyMallStoreSalesList = lazy(() => import('@/pages/mall/Statement/MallStoreSalesList').then((module) => module));
const LazyCalculateList = lazy(() => import('@/pages/mall/Statement/CalculateList').then((module) => module));
const LazyVATReportList = lazy(() => import('@/pages/mall/Statement/VATReportList').then((module) => module));
const LazyTaxBillList = lazy(() => import('@/pages/mall/Statement/TaxBillList').then((module) => module));
const LazyManagementSalesDeposit = lazy(() =>
    import('@/pages/mall/Statement/ManagementSalesDeposit').then((module) => module),
);

const LazyRecommendProducts = lazy(() => import('@/pages/mall/RecommendProducts').then((module) => module));

const LazyUnitedSignUp = lazy(() => import('@/pages/Register/United/UnitedSignUp').then((module) => module));
const LazyDeleteAccount = lazy(() => import('@/pages/DeleteAccount').then((module) => module));

// 소싱 페이지
const LazyRequestList = lazy(() => import('@/pages/sourcing/RequestList').then((module) => module));
const LazySourcingPoints = lazy(() => import('@/pages/sourcing/Points').then((r) => r));
const LazyRegisterVideo = lazy(() => import('@/pages/sourcing/RegisterVideo').then((r) => r));
const LazyMySourcingVideos = lazy(() => import('@/pages/sourcing/MySourcingVideos').then((r) => r));
const LazyEditMyVideo = lazy(() => import('@/pages/sourcing/EditMyVideo').then((r) => r));

const LazyPublicHome = lazy(() => import('@/pages/Public/Home').then((module) => module));

import { dialogOpen } from '@/modules/MuiDialog';
import DragAndDrop from '@/pages/TemplateDemo/DragAndDrop';
import { getAllTags, getCategories, getPickCategories } from '@/services/category.service';
import { getCustomerList, getInquiryList } from '@/services/customer.service';
import {
    getContractList,
    getDDMLiveSchedule,
    getGroupSalesOrdersByGroup,
    getSodomallContractList,
    getSodomallOrderList,
} from '@/services/matching.service';
import { checkOpenState } from '@/services/open-mall.service';
import {
    getIssueOrderList,
    getOrderGroupList,
    GetOrderGroupListParams,
    GetOrderListParams,
    getRealtimeOrderData,
    getRefundOrderList,
    getReturningOrderList,
    getShippingList,
} from '@/services/order.service';
// import * as module from "module";
import { env } from '@/constant/configure';
import PublicLayout from '@/layouts/PublicLayout';
import StudioLayout from '@/layouts/StudioLayout';
import Chat from '@/pages/mall/Chat';
import RegisterStep from '@/pages/RegisterStep';
import PartnersSalesPage from '@/pages/studio/editProfileLink/tabs/component/PartnersSalesPage';
import PickPage from '@/pages/studio/pick';
import { getIsUnreadChat } from '@/services/chat.service';
import { getEventBannerList } from '@/services/event.service';
import {
    getPartnersProductList,
    getPartnersProductListWithBlockId,
    getPartnersRevenue,
    getUserMallBusinessInfo,
} from '@/services/partners.service';
import { getRecommendProducts } from '@/services/recommend.service';
import { getSalesDataByContract, getSalesDataByDate, getSelectableContractList } from '@/services/sales.service';
import { getDesignDataV2, getEventBlockV2, getSpaceUserData, getThemeList } from '@/services/studio.service';
import isMobile, { checkMobileOS } from '@/utils/checkMobile';
import { RequestQueryBuilder } from '@nestjsx/crud-request';
import liveScheduleStore from '@/store/modules/liveScheduleStore';
import { useUserStore } from '@/store';
const LazyEditProfileLink = lazy(() => import('@/pages/studio/editProfileLink').then((module) => module));
const LazySodomall = lazy(() => import('@/pages/studio/sodomall/index').then((module) => module));
const LazyProfileSetting = lazy(() => import('@/pages/studio/profileSetting').then((module) => module));
const LazyLiveScheduleReg = lazy(() => import('@/pages/studio/live/schedule-reg').then((module) => module));
const LazyLiveScheduleList = lazy(() => import('@/pages/studio/live/schedule-list').then((module) => module));
const LazySampleOrderPage = lazy(() => import('@/pages/studio/live/SampleOrderPage').then((module) => module));
const LazySampleOrderPaymentResult = lazy(() =>
    import('@/pages/studio/live/SampleOrderPaymentResult').then((module) => module),
);

export const router = createBrowserRouter([
    {
        path: '/',
        element: <DDMLayout />,
        errorElement: <Error />,
        children: [
            {
                path: '',
                element: <RootCatalog />,
                loader: async () => {
                    // 소도몰 파트너 도메인인 경우 로그인 페이지로 리다이렉트
                    if (isSodomallPartnerDomain()) {
                        return redirect('/login');
                    }
                    await syncSessionData();
                    return null;
                },
            },
            {
                path: '/index.html',
                loader: async () => {
                    return redirect(`/`);
                },
            },
            {
                path: 'switch',
                loader: async (to) => {
                    clearTemporaryToken();
                    const sessionStatus = await checkSwitchSession();
                    await checkMallSession();
                    const { mallId, userId } = sessionStore.getState();
                    console.log('sessionStatus', sessionStatus);
                    // return check ? redirect('/') : redirect(services?.recruit_seller || '/register/terms');
                    switch (sessionStatus) {
                        case 'WAITING':
                        case 'REFUSAL':
                            return redirect('/register/result');
                        case 'UNDEFINED':
                            return redirect('/register/terms');
                        // return redirect(services.recruit_seller || '/register/terms');
                        case 'COMPLETED':
                            return redirect(`/mall/${mallId}`);
                        default:
                            return redirect('/logout');
                    }
                },
            },
            {
                path: 'social/:socialName',
                loader: async ({ params, request }) => {
                    const { socialName = '' } = params;
                    const { url } = request;
                    const queryStr = new URL(url).searchParams.toString();
                    const queryParams: any = queryStr ? qs.parse(queryStr) : undefined;
                    if (!queryParams.code) redirect('/');
                    const { code, name = '', state = '' } = queryParams;
                    try {
                        const check = await checkMallSession();
                        if (check) return redirect(!!(state !== 'null') ? state : '/');
                        const body =
                            socialName === 'apple'
                                ? { social: socialName, code: code, username: name }
                                : { social: socialName, code: code };

                        // 1. socialLogin 내부에서 setSession을 호출하여 sessionStore에 저장
                        const sessionData = await socialLogin(body).then((r) => r);

                        // 2. 추가 사용자 데이터 로드 (userStore, spaceUserStore, spaceUserV2Store 업데이트)
                        let spaceUserData: any = {};
                        try {
                            // 순차적으로 실행하여 store 저장 순서 보장
                            const userData = await getUserData(sessionData.userId);
                            console.log('SNS 로그인 - getUserData 완료:', userData);

                            const spaceData = await getSpaceUserData(sessionData.userId);
                            console.log('SNS 로그인 - getSpaceUserData 완료:', spaceData);
                            spaceUserData = spaceData;
                        } catch (err) {
                            console.warn('사용자 추가 정보 로드 실패:', err);
                        }

                        // 3. 몰 정보 로드 시도 (몰이 있는 경우 mallId 업데이트)
                        try {
                            const { persistenceMallId } = await getOwnMalls(sessionData.accessToken);
                            if (persistenceMallId) {
                                const { setSession } = sessionStore.getState();
                                setSession({
                                    ...sessionData,
                                    mallId: persistenceMallId,
                                });
                                console.log('SNS 로그인 - mallId 업데이트 완료:', persistenceMallId);
                            }
                        } catch (err) {
                            console.warn('몰 정보 로드 실패 (몰이 개설되지 않았을 수 있음):', err);
                        }

                        // 4. sessionStorage persist 저장 완료를 위한 대기 (충분한 시간 확보)
                        console.log('SNS 로그인 - persist 저장 대기 중...');
                        await new Promise((resolve) => setTimeout(resolve, 500));
                        console.log('SNS 로그인 - persist 저장 대기 완료');

                        // 5. 소도몰 역할 확인 및 리다이렉트
                        const { grade_name } = spaceUserV2Store.getState();
                        const isSodomall = grade_name === '소도몰';

                        const checkUserData = !!spaceUserData?.phone_number;
                        if (!!checkUserData) {
                            toast.success('로그인되었습니다.');
                            // 소도몰 역할이고 state가 없거나 홈이면 프로필링크 페이지로 리다이렉트
                            if (isSodomall && (!state || state === 'null' || state === '/')) {
                                return redirect(`/studio/${sessionData.userId}`);
                            }
                            return redirect(!!(state !== 'null') ? state : '/');
                        } else {
                            toast.success('로그인되었습니다. 추가정보 입력이 필요하여 페이지가 이동됩니다.');
                            return redirect(`/${sessionData?.userId}/register-step`);
                        }
                    } catch (error) {
                        return redirect('/');
                    } finally {
                        // console.log('설마')
                        // return redirect(!!(state !== 'null') ? state : '/')
                    }
                },
            },
            {
                path: '/:userId/register-step',
                element: <RegisterStep />,
                loader: async ({ params }) => {
                    const routedUserId = params?.userId || '';
                    const spaceUserData = await getSpaceUserData(routedUserId).catch(() => {
                        return { phone_number: '' };
                    });
                    return {
                        ...spaceUserData,
                    };
                },
            },
            {
                path: 'register',
                element: <MobilePageLayout />,
                loader: async (to) => {
                    try {
                        // /register/sign-up 경로는 세션 체크 없이 바로 이동
                        const pathname = new URL(to.request.url).pathname;
                        if (pathname === '/register/sign-up') {
                            return true;
                        }

                        const checkMall = await checkMallSession();
                        if (checkMall) {
                            return redirect('/');
                        }

                        const checkSession = await checkRefreshToken();
                        console.log('checkSession', checkSession);
                        if (checkSession) {
                            const { accessToken } = await refreshToken();

                            console.log('checkSession-accessToken', accessToken);

                            setTemporaryToken(accessToken);
                            return redirect('/register/terms');
                        }

                        return true;
                    } catch (e: any) {
                        console.error(e);
                        return redirect(`/`);
                    }
                },
                children: [
                    {
                        path: '',
                        element: <Register />,
                    },
                    // {
                    //     path: 'sign-up',
                    //     element: <LazyRouteElement element={<LazySignUp />} />,
                    //     loader: async to => {
                    //         return {
                    //             pageTitle: '플로잉 회원가입',
                    //         }
                    //     },
                    // },

                    {
                        path: 'sign-up',
                        element: <LazyRouteElement element={<LazyUnitedSignUp />} />,
                        loader: async (to) => {
                            return { pageTitle: i18n.t('auth.register'), hideHeader: true };
                        },
                    },
                    {
                        path: 'terms',
                        element: <LazyRouteElement element={<LazyRegisterTerms />} />,
                        loader: async (to) => {
                            const token = getTemporaryToken();
                            if (!token) {
                                const checkSession = await checkRefreshToken();
                                console.log('checkSession', checkSession);
                                if (checkSession) {
                                    const { accessToken } = await refreshToken().catch((err) => {
                                        console.error(err);
                                        return {
                                            accessToken: undefined,
                                        };
                                    });

                                    console.log('check-access-token', accessToken);

                                    if (!!accessToken) {
                                        setTemporaryToken(accessToken);
                                    }

                                    // return !accessToken ? redirect(`${services.flowing_studio}/login`) : {
                                    //     pageTitle: '플로잉 약관동의',
                                    // };
                                    return !accessToken
                                        ? redirect(`/`)
                                        : {
                                              pageTitle: '플로잉 약관동의',
                                          };
                                } else {
                                    return redirect('/register/sign-up');
                                }
                            } else {
                                const registrationState = await checkOpenState(token);
                                if (!!registrationState) {
                                    return redirect('/register/result');
                                }
                            }

                            return {
                                pageTitle: '플로잉 약관동의',
                            };
                        },
                    },
                    {
                        path: 'form',
                        element: <LazyRouteElement element={<LazyRegisterForm />} />,
                        loader: async (to) => {
                            const token = getTemporaryToken();
                            if (!token) {
                                const checkSession = await checkRefreshToken();
                                if (checkSession) {
                                    return redirect(`/`);
                                } else {
                                    return redirect('/register/sign-up');
                                }
                            } else {
                                // todo: token이 있는 경우, token 기반으로 신청상태를 체크하여 상태보기 분기
                            }

                            return {
                                pageTitle: '플로잉 입점신청',
                            };
                        },
                    },
                    {
                        path: 'result',
                        element: <LazyRouteElement element={<LazyRegisterResult />} />,
                        loader: async (to) => {
                            const token = getTemporaryToken();
                            if (!token) {
                                return redirect('/register/terms');
                            } else {
                                const registrationState = await checkOpenState(token);
                                if (!registrationState)
                                    throw {
                                        status: 404,
                                    };

                                return {
                                    pageTitle: '입점신청 결과보기',
                                    state: registrationState,
                                };
                            }
                        },
                    },
                ],
            },
            {
                path: 'logout',
                loader: async (to) => {
                    try {
                        await signOutSession();

                        // 소도몰 파트너 도메인인 경우 로그인 페이지로 리다이렉트
                        if (isSodomallPartnerDomain()) {
                            return redirect(`/login`);
                        }

                        return redirect(`/`);
                    } catch (e) {
                        console.error(e);
                        if (isSodomallPartnerDomain()) {
                            return redirect(`/login`);
                        }
                        return redirect(`/`);
                    }
                },
                // pendingElement: (<div></div>),
            },
            {
                path: 'delete-account',
                element: <MobilePageLayout />,
                children: [
                    {
                        path: '',
                        element: <LazyRouteElement element={<LazyDeleteAccount />} />,
                    },
                ],
                loader: () => {
                    const currentWindow: any = window;
                    return {
                        pageTitle: '회원탈퇴',
                        isDisplayHeader: !currentWindow?.Withdrawal,
                    };
                },
            },
        ],
    },
    {
        path: 'home',
        element: <PublicLayout />,
        loader: async (to) => {
            if (env === 'prd') {
                toast.info('잘못된 접근입니다.');
                return redirect('/');
            }
            // const check = await checkUserSession();
            // if (!!check) {
            //     const { userId } = sessionStore.getState();
            //     return redirect(`/sourcing/${userId}`)
            // }
            return true;
        },
        children: [
            {
                path: '',
                element: <LazyRouteElement element={<LazyPublicHome />} />,
                loader: async (to) => {
                    return defer({
                        eventBannerList: getEventBannerList().catch(() => []),
                    });
                },
            },
        ],
    },
    {
        path: '/studio/spacehome',
        loader: () => {
            return redirect(`/`);
        },
    },
    {
        path: '/studio/spacehome-block',
        loader: () => {
            return redirect(`/`);
        },
    },
    {
        path: 'sample-order/payment-result',
        errorElement: <Error />,
        element: <LazyRouteElement element={<LazySampleOrderPaymentResult />} />,
        loader: async () => {
            // status 쿼리 파라미터만으로 결과 표시, API 호출 불필요
            return null;
        },
    },
    {
        path: '/sodomall/:userId',
        id: '/sodomall/:userId',
        element: <StudioLayout />,
        errorElement: <Error />,
        loader: async (to) => {
            const routedUserId = to.params?.userId || '';

            const spaceUserData = await getSpaceUserData(routedUserId);

            const designData = await getDesignDataV2(routedUserId);

            const blockData = await getEventBlockV2(routedUserId).catch(() => {
                return [];
            });
            return {
                designData,
                blockData,
                spaceUserData,
            };
        },
        children: [
            {
                path: '',
                errorElement: <Error />,
                element: <LazyRouteElement element={<LazySodomall />} />,
                loader: async (to) => {
                    const routedUserId = to.params?.userId || '';
                    try {
                        const queryParams = new URL(to.request?.url).searchParams;

                        const userData = await getSpaceUserData(routedUserId);

                        const designData = await getDesignDataV2(routedUserId);

                        const blockData = await getEventBlockV2(routedUserId).catch(() => {
                            return [];
                        });
                        const wholeTheme = await getThemeList('');
                        return {
                            designData,
                            blockData,
                            userData,
                            wholeTheme,
                        };
                    } catch (e: any) {
                        console.log(e);
                        throw e;
                    }
                },
            },
        ],
    },
    {
        path: '/studio/:userId',
        id: '/studio/:userId',
        element: <StudioLayout />,
        errorElement: <Error />,
        loader: async (to) => {
            if (isMobile() && env === 'prd') {
                dialogOpen({
                    message: `${'이 웹 사이트는 PC 화면에 최적화되어 있습니다.'} <br/> ${'모바일 버전은 앱 사용을 권장드립니다.'} <br/> ${'앱 설치를 위해 스토어로 이동하시겠습니까?'}`,
                    confirmText: '확인',
                    onConfirmed: async () => {
                        await linkAppStore();
                    },
                    closeText: '취소',
                    onClose: async () => {
                        return false;
                    },
                    isAllowHtml: true,
                });
                return;
            }

            // const check = await checkUserSession();
            const { userId, accessToken } = sessionStore.getState();
            const check = accessToken && userId;

            // const check = true;
            if (!check) {
                // 로그인이 필요한 경우 홈으로 리다이렉트 (로그인 팝업은 DDMNavBar에서 처리)
                toast.error('로그인이 필요한 페이지입니다.', {
                    toastId: 'page-permission-toast',
                    autoClose: 1500,
                });
                return redirect('/');
            } else {
                // if (to?.params?.userId && userId && to?.params?.userId !== userId) return redirect(`/studio/${userId}`);

                try {
                    const userData = await getUserData(userId);
                    const spaceUserData = await getSpaceUserData(userId)
                        .then((r) => r)
                        .catch(() => {});
                    if (Object.keys(spaceUserData).length === 0) {
                        toast.success('로그인되었습니다. 추가정보 입력이 필요하여 페이지가 이동됩니다.');
                        return redirect(`/${userId}/register-step`);
                    }
                    return { userData, spaceUserData, unreadOfferCount: 0 };
                } catch (error) {
                    toast.success('추가정보 입력이 필요하여 페이지가 이동됩니다.');
                    return redirect(`/${userId}/register-step`);
                }
            }
        },
        children: [
            {
                path: '',
                errorElement: <Error />,
                element: <LazyRouteElement element={<DynamicStudioComponent />} />,
                loader: async (to) => {
                    const routedUserId = to.params?.userId || '';
                    try {
                        const queryParams = new URL(to.request?.url).searchParams;
                        const tab = queryParams.get('tab');
                        const subPageType = queryParams.get('subPageType');
                        if (!tab) {
                            return redirect(`/studio/${routedUserId}?tab=block`);
                        }
                        const userData = await getSpaceUserData(routedUserId);

                        const designData = await getDesignDataV2(routedUserId);

                        const blockData = await getEventBlockV2(routedUserId).catch(() => {
                            return [];
                        });
                        const wholeTheme = await getThemeList('');

                        if (tab === 'block') {
                            return {
                                designData,
                                blockData,
                                userData,
                                wholeTheme,
                            };
                        } else if (tab === 'partners') {
                            if (!!(subPageType === 'partners-sales')) {
                                return {};
                            }
                            const subTab = queryParams?.get('subTab') || 'products';
                            if (subTab === 'products') {
                                const block_id = String(queryParams.get('block') || '');
                                const { categories, accessoryCategories, fashionCategories, livingCategories } =
                                    await getPickCategories();
                                return {
                                    categories,
                                    accessoryCategories,
                                    fashionCategories,
                                    livingCategories,
                                    productData: getPartnersProductList({ user_id: routedUserId }),
                                    addedProducts: !!block_id
                                        ? getPartnersProductListWithBlockId({
                                              user_id: routedUserId,
                                              block_id,
                                          })
                                        : [],
                                };
                            } else if (subTab === 'admin') {
                                return {
                                    revenueData: getPartnersRevenue({ user_id: routedUserId }),
                                    accountInfo: getUserMallBusinessInfo(),
                                };
                            } else {
                                return {};
                            }
                        } else if (tab === 'analysis') {
                            return {};
                        } else if (tab === 'ads') {
                            return {};
                        } else {
                            return {};
                        }
                    } catch (e: any) {
                        console.log(e);
                        throw e;
                    }
                },
            },
            {
                path: 'live/schedule-reg',
                errorElement: <Error />,
                element: <LazyRouteElement element={<LazyLiveScheduleReg />} />,
                loader: async (to) => {
                    const { setCurrentSchedule } = liveScheduleStore.getState();
                    const userId = to.params['userId'] || '';
                    const queryParams = new URL(to.request.url).searchParams;
                    const scheduleId = queryParams.get('scheduleId');

                    const qb = RequestQueryBuilder.create();

                    // scheduleId가 있으면 해당 ID로 조회, 없으면 activatedAt notnull로 조회
                    if (scheduleId) {
                        qb.setFilter({ field: 'id', operator: '$eq', value: scheduleId });
                    } else {
                        qb.setFilter({ field: 'activatedAt', operator: '$notnull' });
                    }

                    const [currentSchedule] = await getDDMLiveSchedule({ userId, query: qb.query() });
                    setCurrentSchedule(currentSchedule);

                    return {
                        currentSchedule,
                    };
                },
            },
            {
                path: 'live/schedule-list',
                errorElement: <Error />,
                element: <LazyRouteElement element={<LazyLiveScheduleList />} />,
                loader: async (to) => {
                    return true;
                },
            },
            {
                path: 'sample-order',
                errorElement: <Error />,
                element: <LazyRouteElement element={<LazySampleOrderPage />} />,
                loader: async (to) => {
                    const { setCurrentSchedule } = liveScheduleStore.getState();
                    const userId = to.params['userId'] || '';
                    const queryParams = new URL(to.request.url).searchParams;
                    const scheduleId = queryParams.get('scheduleId');

                    if (!scheduleId) {
                        toast.error('스케줄 정보가 없습니다.');
                        return redirect(`/studio/${userId}/live/schedule-list`);
                    }

                    const qb = RequestQueryBuilder.create();
                    qb.setFilter({ field: 'id', operator: '$eq', value: scheduleId });
                    qb.setJoin({ field: 'ddmLiveScheduleProductMappers' });
                    qb.setJoin({ field: 'ddmLiveScheduleProductMappers.product' });
                    qb.setJoin({ field: 'ddmLiveScheduleProductMappers.product.ddmProductOptions' });
                    qb.setJoin({ field: 'ddmLiveScheduleProductMappers.productGroup' });
                    qb.setJoin({ field: 'ddmLiveScheduleProductMappers.productGroup.vendor' });
                    qb.setJoin({ field: 'ddmLiveScheduleProductMappers.productGroup.vendor.vendorBusinessInfo' });
                    qb.setJoin({ field: 'ddmSampleRequests' });

                    const [currentSchedule] = await getDDMLiveSchedule({ userId, query: qb.query() });

                    if (!currentSchedule) {
                        toast.error('스케줄 정보를 찾을 수 없습니다.');
                        return redirect(`/studio/${userId}/live/schedule-list`);
                    }

                    setCurrentSchedule(currentSchedule);

                    return {
                        currentSchedule,
                    };
                },
            },
            {
                path: 'pick',
                errorElement: <Error />,
                element: <LazyRouteElement element={<PickPage />} />,
                loader: async (to) => {
                    const {
                        categories,
                        accessoryCategories,
                        fashionCategories,
                        livingCategories,
                        athleisureCategories,
                        foodCategories,
                        supplementCategories,
                    } = await getPickCategories();

                    const userId = to.params['userId'];
                    const blockData = (await getEventBlockV2(userId!)) as any[];
                    const partnersBlocks = blockData?.filter?.((block) => block?.['block_type'] == 'partners');
                    const getPartnersProductListPromises = partnersBlocks.map((block) =>
                        getPartnersProductListWithBlockId({
                            user_id: userId!,
                            block_id: block?.block_id,
                        })
                            .then((partnersProducts) => ({
                                blockId: block?.block_id,
                                productIds: partnersProducts?.map(({ product_id }) => product_id) || [],
                            }))
                            .catch((e) => {
                                console.error(e);
                                return {
                                    blockId: block?.block_id,
                                    productIds: [],
                                };
                            }),
                    );

                    const results = await Promise.all(getPartnersProductListPromises);

                    const partnersBlockProductIds = results.reduce(
                        (acc, { blockId, productIds }) => {
                            acc[blockId] = productIds;
                            return acc;
                        },
                        {} as Record<string, string[]>,
                    );
                    const addedProducts = new Set(Object.values(partnersBlockProductIds).flat());
                    return {
                        categories,
                        blockData,
                        partnersBlocks,
                        partnersBlockProductIds,
                        addedProducts,
                        livingCategories,
                        fashionCategories,
                        accessoryCategories,
                        athleisureCategories,
                        foodCategories,
                        supplementCategories,
                    };
                },
            },
            {
                path: 'pick-stats',
                errorElement: <Error />,
                element: <LazyRouteElement element={<PartnersSalesPage />} />,
                loader: async (to) => {
                    return {};
                },
            },
            {
                path: 'user-setting',
                errorElement: <Error />,
                element: <LazyRouteElement element={<LazyProfileSetting />} />,
                loader: async (to) => {
                    return true;
                },
            },
        ],
    },
    /* {
        path: '/sourcing/:userId',
        element: <SourcingLayout />,
        errorElement: <Error />,
        id: 'sourcing-root',
        loader: async (to) => {
            try {
                const check = await checkUserSession();
                if (!check) {
                    toast.error('로그인이 필요한 페이지입니다.', {
                        toastId: 'page-permission-toast',
                        autoClose: 1500,
                    });
                    let refer;

                    const url = new URL(to.request?.url || '');
                    console.log(url);
                    const credential = to.request?.credentials;

                    if (credential === 'same-origin' && !!url.pathname) refer = url.pathname;

                    return redirect(`/login${!!refer ? `?refer=${refer}` : ''}`);
                } else {
                    const userId = to?.params?.userId || '';
                    const spaceUserData = await getSpaceUserData(userId)
                        .then((r) => r)
                        .catch(() => {});
                    const isUserIdExist = String(userStore.getState().id || '') === userId;
                    if (!isUserIdExist) {
                        await getDesignDataV2(userId)
                            .then((r) => r)
                            .catch(() => {
                                return {};
                            });
                        await getUserData(userId).catch(() => {});
                    }
                    return { ...spaceUserData };
                }
            } catch (error) {
                throw error;
            }
        },
        children: [
            {
                path: '',
                element: <Introduction />,
                loader: async (to) => {
                    const userId = to?.params?.userId || '';
                    try {
                        await getMallUserIdByToken()
                            .then((r) => r)
                            .catch(() => {});
                        return defer({
                            shortsList: getShortsList().catch(() => []),
                            sourcingList2: getSourcingListWithVendor(userId, { offset: 0, limit: 7 }).catch(() => {
                                return {
                                    list: [],
                                    total: 0,
                                };
                            }),
                            productLists: getSourcingProductList(userId).catch(() => []),
                        });
                    } catch (error) {
                        return {};
                    }
                },
            },
            {
                path: 'search',
                element: <SourcingSearch />,
                loader: async (to) => {
                    const { userId = '' } = to.params;
                    const queryStr = new URL(to.request.url).searchParams.toString();
                    const queryParams = queryStr ? qs.parse(queryStr) : undefined;
                    const value = String(queryParams?.value || '');
                    const category = String(queryParams?.category || '');
                    // if (value.trim() === '') {
                    //     toast.warning('검색어를 입력해주세요.', { toastId: 'require search value' })
                    //     return {
                    //         searchResult: []
                    //     }
                    // }
                    console.log('search value params::::', queryParams);
                    const offset = Number(queryParams?.offset || 0);
                    const limit = Number(queryParams?.limit || 36);
                    const shortsId = String(queryParams?.queryId || '');
                    try {
                        return defer({
                            categoryList: getShortsCategoryList().catch(() => {
                                return [];
                            }),
                            searchResult: getSourcingSearchList(value, offset, limit, category).catch(() => {
                                return [];
                            }),
                            hashtags: !!shortsId
                                ? getShortsVideoById(shortsId)
                                      .then((r) => {
                                          return r?.hashtags || [];
                                      })
                                      .catch(() => [])
                                : [],
                        });
                    } catch (error) {
                        console.log(error);
                        toast.error('검색 결과를 불러오지 못하였습니다. 잠시 후 다시 시도해주세요.', {
                            toastId: 'load fail',
                        });
                        return { searchResult: [] };
                    }
                },
            },
            {
                path: 'request-list',
                element: <LazyRouteElement element={<LazyRequestList />} />,
                loader: async (to) => {
                    try {
                        const userId = to?.params?.userId || '';
                        const queryStr = new URL(to.request.url).searchParams.toString();
                        const queryParams = queryStr ? qs.parse(queryStr) : undefined;
                        const offset = Number(queryParams?.offset || 0);
                        const limit = Number(queryParams?.limit || 15);
                        const filterType = String(queryParams?.filterType || 'all');
                        if (limit > 15) {
                            const query = {
                                ...queryParams,
                                limit: 15,
                            };
                            return redirect(`?${qs.stringify(query)}`);
                        }
                        return defer({
                            // sourcingList: getSourcingList(userId, { offset, limit, filterType }).catch(() => {
                            //     toast.error('소싱 요청 목록을 불러오지 못하였습니다.', { toastId: 'fail load request list' })
                            //     return { list: [], total: 0 }
                            // }),
                            sourcingList2: getSourcingListWithVendor(userId, { offset, limit, filterType }).catch(
                                () => {
                                    toast.error('소싱 요청 목록을 불러오지 못하였습니다.', {
                                        toastId: 'fail load request list',
                                    });
                                    return { list: [], total: 0 };
                                },
                            ),
                        });
                    } catch (error) {
                        throw error;
                    }
                },
            },
            {
                path: 'sourcing-chat',
                element: <Chat />,
                loader: async () => {
                    try {
                        const mallUserId = await getMallUserIdByToken().catch(() => {});
                        return { mallUserId };
                    } catch (error) {
                        return { mallUserId: '' };
                    }
                },
            },
            {
                path: 'points/:type',
                element: <LazyRouteElement element={<LazySourcingPoints />} />,
                loader: async (to) => {
                    const userId = to?.params?.userId || '';
                    const type = to?.params?.type || 'watch-history';
                    const queryStr = new URL(to.request.url).searchParams.toString();
                    const queryParams = queryStr ? qs.parse(queryStr) : undefined;
                    const limit = Number(queryParams?.limit || 10) || 0;
                    const offset = Number(queryParams?.offset || 0) || 0;
                    try {
                        const userPoint = await getUserPoints(userId).catch(() => 0);
                        if (type === 'points-history') {
                            const filterType = String(queryParams?.filterType || 'all');
                            return defer({
                                userPoint,
                                historyList: getUserPointsHistory(userId, limit, offset, filterType).then((r) => {
                                    console.log('history result:::', r);
                                    return r;
                                }),
                            });
                        } else if (type === 'watch-history') {
                            return defer({
                                userPoint,
                            });
                        } else if (type === 'points-draw') {
                            return defer({
                                userPoint,
                            });
                        } else if (type === 'use-point') {
                            return defer({
                                userPoint,
                                gifticonCategories: getGifticonCategories(),
                            });
                        } else {
                            return true;
                        }
                    } catch (error) {
                        throw error;
                    }
                },
            },
            {
                path: 'register-video',
                element: <LazyRouteElement element={<LazyRegisterVideo />} />,
                loader: async () => {
                    return true;
                },
            },
            {
                path: 'my-videos',
                element: <LazyRouteElement element={<LazyMySourcingVideos />} />,
                loader: async (to) => {
                    const userId = to.params?.userId || '-';
                    const queryString = new URL(to.request.url).searchParams.toString();
                    const queryParams = queryString ? qs.parse(queryString) : undefined;
                    if (!queryParams?.offset) {
                        return redirect(`/sourcing/${userId}/my-videos?offset=0&limit=25&tab=my&search=`);
                    }
                    return defer({
                        posts: getPostsByAdmin({
                            limit: Number(queryParams?.limit || 25),
                            offset: Number(queryParams?.offset || 0),
                            collectionName: String(queryParams?.search || ''),
                        }).catch(() => {
                            toast.error('목록을 조회하지 못했습니다.', { toastId: 'load-fail' });
                            return {
                                totalCount: 0,
                                itemList: [],
                            };
                        }),
                    });
                },
            },
            {
                path: 'my-videos/:postsId',
                element: <LazyRouteElement element={<LazyEditMyVideo />} />,
                loader: async (to) => {
                    const postsId = to.params?.postsId || '-';
                    return defer({
                        items: getPost({ postsId }).catch(() => {
                            return { isLoaded: 'fail' };
                        }),
                    });
                },
            },
        ],
    }, */
    {
        path: '/mall/:mallId',
        element: <ManagerLayout />,
        errorElement: <Error />,
        id: 'mall-root',
        loader: async (to) => {
            // check session & permission
            const routedMallId = to.params?.mallId || '';

            console.log('%c access-check', 'color:red;background-color:rgba(255,0,0,0.3)');

            console.log('%c access-check2', 'color:red;background-color:rgba(255,0,0,0.3)');

            const check = await checkMallSession();
            console.log('checked', check);
            if (!check) {
                // 로그인이 필요한 경우 홈으로 리다이렉트 (로그인 팝업은 DDMNavBar에서 처리)
                toast.error('로그인이 필요한 페이지입니다.', {
                    toastId: 'page-permission-toast',
                    autoClose: 1500,
                });
                return redirect('/');
            } else {
                const { mallId } = sessionStore.getState();
                const isPermitted = mallId ? await checkMallPermission(routedMallId) : false;

                const mallInfo = await getMallInfo(routedMallId).catch((err) => {
                    if (err.status !== 403) {
                        return {
                            status: 404,
                            message: '해당 상점 정보를 찾을 수 없습니다.',
                        };
                    }
                });
                const mallUserId = await getMallUserIdByToken().catch(() => '');
                const isUnreadChat = await getIsUnreadChat(mallUserId);
                const { unansweredCount } = await getNewInquiryCount(mallId).catch(() => 0);
                const isReturnIssueAction = await getCheckIsReturnIssue(mallId);

                if (mallId === to.params?.mallId || !!isPermitted) {
                    return {
                        ...mallInfo,
                        isInquiryNew: unansweredCount > 0,
                        isReturnIssueAction,
                        isUnreadChat,
                    };
                } else {
                    // console.log('%c access-error', 'color:red;background-color:rgba(255,0,0,0.3)', {mallInfo});
                    throw {
                        status: 403,
                        message: '해당 몰에 접근 권한이 없음',
                    };

                    // toast.warning('해당 몰에 접근 권한이 없음', {
                    //     toastId: 'page-permission-toast',
                    // });
                    // return redirect('/');
                }
            }
        },
        children: [
            {
                path: '',
                element: <BusinessInfo />,
                loader: async (to) => {
                    const routedMallId = to.params?.mallId || '';

                    return defer({
                        brandInfoData: await getMallInfo(routedMallId),
                        businessData: getBusinessInfo(routedMallId).then((r) => r),
                    });
                },
            },
            {
                path: 'policy/sales-policy',
                // element: <SalesPolicy />,
                element: <LazyRouteElement element={<LazySalesPolicy />} />,
                loader: async (to) => {
                    try {
                        const mallId = to.params.mallId || '';
                        return await getSalesPolicy(mallId);
                    } catch (e) {
                        throw e;
                    }
                },
            },
            {
                path: 'policy/shipping-policy',
                // element: <ShippingPolicy />,
                element: <LazyRouteElement element={<LazyShippingPolicy />} />,
                id: 'shipping-policy-router',
                loader: async (to) => {
                    try {
                        const mallId = to.params.mallId || '';

                        return await getShippingPolicyList(mallId);
                    } catch (e) {
                        toast.error('배송정보 목록을 불러오는데 실패했습니다.');
                        return [];
                        // throw e;
                    }
                },
            },
            {
                path: 'tag-manager',
                element: <LazyRouteElement element={<LazyTagManagement />} />,
                loader: async (to) => {
                    try {
                        const { mallId } = to.params;

                        return await getAllTags(mallId || '');
                    } catch (e: any) {
                        if (e?.placeHolder || e?.data?.placeHolder) {
                            toast.error(e?.placeHolder || e?.data?.placeHolder);
                        } else {
                            toast.error('쇼핑몰 태그목록을 불러오지 못했습니다.');
                            console.error(e);
                        }
                        return [];
                    }
                },
            },
            {
                path: 'products',
                element: <LazyRouteElement element={<LazyProducts />} />,
                loader: async (to) => {
                    try {
                        const { mallId = '' } = to.params;
                        const queryStr = new URL(to.request.url).searchParams.toString();
                        const queryParams = queryStr ? qs.parse(queryStr) : undefined;

                        return await getProductList(mallId, queryParams as GetProductParams | undefined);
                    } catch (e) {
                        toast.error('상품목록을 불러오는데 실패했습니다.', {
                            toastId: 'products-data-load-error-toast',
                        });
                        console.error(e);
                        return {
                            list: [],
                            total: 0,
                        };
                    }
                },
            },
            {
                path: 'group-sale-products',
                element: <LazyRouteElement element={<LazyGroupSaleProducts />} />,
                loader: async (to) => {
                    try {
                        const { userId = '-' } = sessionStore.getState();

                        console.log('userId!!', userId);

                        const { mallId = '' } = to.params;
                        const queryStr = new URL(to.request.url).searchParams.toString();
                        const queryParams = queryStr ? qs.parse(queryStr) : undefined;

                        // return await getProductList(mallId, queryParams as GetProductParams | undefined);

                        const aa = await getContractList(userId, queryParams);
                        console.log('aa', aa);

                        return aa;
                    } catch (e: any) {
                        if (e.data?.placeHolder || e?.placeHolder) {
                            toast.error(e.data?.placeHolder || e?.placeHolder, {
                                toastId: 'products-data-load-error-toast',
                            });
                        } else {
                            toast.error('상품목록을 불러오는데 실패했습니다.', {
                                toastId: 'products-data-load-error-toast',
                            });
                        }

                        console.error(e);
                        return {
                            list: [],
                            total: 0,
                        };
                    }
                },
            },
            {
                path: 'products/create',
                element: <LazyRouteElement element={<LazyProductEditor />} />,
                loader: async (to) => {
                    try {
                        const { mallId = '' } = to.params;

                        return {
                            shippingPolicyData: await getShippingPolicyList(mallId).then((r) => r),
                        };
                    } catch (e) {
                        throw e;
                    }
                },
            },
            {
                path: 'products/:productId',
                element: <LazyRouteElement element={<LazyProductEditor />} />,
                loader: async (to) => {
                    try {
                        const { mallId = '', productId = '' } = to.params;
                        const shippingPolicyData = await getShippingPolicyList(mallId);
                        const productData = await getProduct(mallId, productId);

                        return { shippingPolicyData, productData };
                    } catch (e: any) {
                        if (e.data?.message && e.data?.message === 'No data exists.') {
                            throw {
                                status: 404,
                                message: 'Page Not Founded!',
                            };
                        } else {
                            throw e;
                        }
                    }
                },
            },
            {
                path: 'order-groups',
                element: <LazyRouteElement element={<LazyOrderGroups />} />,
                loader: async (to) => {
                    try {
                        const { mallId = '' } = to.params;
                        const queryStr = new URL(to.request.url).searchParams.toString();
                        const queryParams = queryStr ? qs.parse(queryStr) : undefined;

                        // return await getOrderList(mallId, queryParams as GetOrderListParams | undefined);
                        const { totalAmount } = await getRealtimeOrderData(mallId)
                            .then((r) => r)
                            .catch(() => {
                                toast.error('실시간 주문현황을 불러오는 데 실패하였습니다.', {
                                    toastId: 'realTime-load-fail',
                                });
                                return {
                                    totalAmount: {
                                        orderCount: 0,
                                        proceedOrderCount: 0,
                                        proceedProductCount: 0,
                                        productCount: 0,
                                    },
                                };
                            });
                        return {
                            ...(await getOrderGroupList(mallId, queryParams as GetOrderGroupListParams | undefined)),
                            realtimeStatus: totalAmount,
                        };
                    } catch (e) {
                        toast.error('주문목록을 불러오는데 실패했습니다.', {
                            toastId: 'order-group-data-load-error-toast',
                        });
                        console.error(e);
                        return {
                            list: [],
                            total: 0,
                        };
                    }
                },
            },
            {
                path: 'group-sales-orders',
                element: <LazyRouteElement element={<LazyGroupSalesOrders />} />,
                loader: async (to) => {
                    const errorToastId = 'order-group-data-load-error-toast';

                    try {
                        const { userId = '-' } = sessionStore.getState();

                        const queryStr = new URL(to.request.url).searchParams.toString();
                        const queryParams = queryStr ? qs.parse(queryStr) : undefined;

                        return await getGroupSalesOrdersByGroup(userId, queryParams);
                        // return await getGroupSalesOrders(userId, queryParams);
                    } catch (e: any) {
                        if (e.data?.placeHolder || e?.placeHolder) {
                            toast.error(e.data?.placeHolder || e?.placeHolder, {
                                toastId: errorToastId,
                            });
                        } else {
                            toast.error('주문목록을 불러오는데 실패했습니다.', {
                                toastId: errorToastId,
                            });
                        }

                        console.error(e);
                        return {
                            list: [],
                            total: 0,
                        };
                    }
                },
            },
            {
                path: 'group-sale-products-sodomall',
                element: <LazyRouteElement element={<LazyGroupSaleProductsSodomall />} />,
                loader: async (to) => {
                    try {
                        const { userId = '-' } = sessionStore.getState();

                        const queryStr = new URL(to.request.url).searchParams.toString();
                        const queryParams = queryStr ? qs.parse(queryStr) : undefined;

                        return await getSodomallContractList(userId, queryParams);
                    } catch (e: any) {
                        if (e.data?.placeHolder || e?.placeHolder) {
                            toast.error(e.data?.placeHolder || e?.placeHolder, {
                                toastId: 'products-data-load-error-toast',
                            });
                        } else {
                            toast.error('상품목록을 불러오는데 실패했습니다.', {
                                toastId: 'products-data-load-error-toast',
                            });
                        }

                        console.error(e);
                        return {
                            list: [],
                            total: 0,
                        };
                    }
                },
            },
            {
                path: 'create-contract-sodomall',
                element: <LazyRouteElement element={<LazyCreateContractSodomall />} />,
                loader: async () => {
                    return true;
                },
            },
            {
                path: 'group-sales-orders-sodomall',
                element: <LazyRouteElement element={<LazyGroupSalesOrdersSodomall />} />,
                loader: async (to) => {
                    const errorToastId = 'order-group-data-load-error-toast';

                    try {
                        const { userId = '-' } = sessionStore.getState();

                        const queryStr = new URL(to.request.url).searchParams.toString();
                        const queryParams = queryStr ? qs.parse(queryStr) : {};

                        // 필터에서 보내는 파라미터 파싱
                        const ordererNameParam = queryParams.orderer_name as Record<string, string> | undefined;
                        const phoneNumberParam = queryParams.phone_number as Record<string, string> | undefined;
                        const productNameParam = queryParams.product_name as Record<string, string> | undefined;
                        const orderStateTypeParam = queryParams.order_state_type as
                            | Record<string, string[]>
                            | undefined;
                        const orderedAtParam = queryParams.ordered_at as Record<string, string> | undefined;

                        // 새로운 소도몰 전용 API 사용
                        return await getSodomallOrderList(userId, {
                            offset: parseInt(queryParams.offset as string) || 0,
                            limit: parseInt(queryParams.limit as string) || 15,
                            ordererName: ordererNameParam?.like || undefined,
                            phoneNumber: phoneNumberParam?.like || undefined,
                            productName: productNameParam?.like || undefined,
                            orderStateTypes:
                                orderStateTypeParam?.in && orderStateTypeParam.in.length > 0
                                    ? orderStateTypeParam.in
                                    : undefined,
                            startDate: orderedAtParam?.ge || undefined,
                            endDate: orderedAtParam?.le || undefined,
                        });
                    } catch (e: any) {
                        if (e.data?.placeHolder || e?.placeHolder) {
                            toast.error(e.data?.placeHolder || e?.placeHolder, {
                                toastId: errorToastId,
                            });
                        } else {
                            toast.error('주문목록을 불러오는데 실패했습니다.', {
                                toastId: errorToastId,
                            });
                        }

                        console.error(e);
                        return {
                            list: [],
                            total: 0,
                        };
                    }
                },
            },
            {
                path: 'shipping-managements',
                element: <LazyRouteElement element={<LazyShippingManagements />} />,
                loader: async (to) => {
                    try {
                        const { mallId = '' } = to.params;
                        const queryStr = new URL(to.request.url).searchParams.toString();
                        const queryParams = queryStr ? qs.parse(queryStr) : undefined;
                        return await getShippingList(mallId, queryParams as GetOrderListParams | undefined);
                    } catch (e) {
                        toast.error('배송대상 목록을 불러오는데 실패했습니다.', {
                            toastId: 'shipping-order-data-load-error-toast',
                        });
                        console.error(e);
                        return {
                            list: [],
                            total: 0,
                        };
                    }
                },
            },
            {
                path: 'return-orders',
                element: <LazyRouteElement element={<LazyReturnOrders />} />,
                loader: async (to) => {
                    try {
                        const { mallId = '' } = to.params;
                        const queryStr = new URL(to.request.url).searchParams.toString();
                        const queryParams = queryStr ? qs.parse(queryStr) : undefined;

                        return await getReturningOrderList(mallId, queryParams as GetOrderListParams | undefined);
                    } catch (e) {
                        toast.error('환불 및 취소 목록을 불러오는데 실패했습니다.', {
                            toastId: 'issued-order-data-load-error-toast',
                        });
                        console.error(e);
                        return {
                            list: [],
                            total: 0,
                        };
                    }
                },
            },
            {
                path: 'issue-orders',
                element: <LazyRouteElement element={<LazyIssueOrders />} />,
                loader: async (to) => {
                    try {
                        const { mallId = '' } = to.params;
                        const queryStr = new URL(to.request.url).searchParams.toString();
                        const queryParams = queryStr ? qs.parse(queryStr) : undefined;

                        return await getIssueOrderList(mallId, queryParams as GetOrderListParams | undefined);
                    } catch (e) {
                        toast.error('취소 / 반품 / 교환 목록을 불러오는데 실패했습니다.', {
                            toastId: 'issued-order-data-load-error-toast',
                        });
                        console.error(e);
                        return {
                            list: [],
                            total: 0,
                        };
                    }
                },
            },
            {
                path: 'refund-orders',
                element: <LazyRouteElement element={<LazyRefundOrders />} />,
                loader: async (to) => {
                    try {
                        const { mallId = '' } = to.params;
                        const queryStr = new URL(to.request.url).searchParams.toString();
                        const queryParams = queryStr ? qs.parse(queryStr) : undefined;

                        return await getRefundOrderList(mallId, queryParams as GetOrderListParams | undefined).then(
                            (r) => {
                                console.log(r);
                                return r;
                            },
                        );
                    } catch (e) {
                        toast.error('환불 목록을 불러오는데 실패했습니다.', {
                            toastId: 'issued-order-data-load-error-toast',
                        });
                        console.error(e);
                        return {
                            list: [],
                            total: 0,
                        };
                    }
                },
            },
            {
                path: 'customers',
                element: <LazyRouteElement element={<LazyCustomers />} />,
                loader: async (to) => {
                    try {
                        const { mallId = '' } = to.params;
                        const queryStr = new URL(to.request.url).searchParams.toString();
                        const queryParams: any = queryStr ? qs.parse(queryStr) : undefined;
                        if (!queryParams?.offset) {
                            return redirect(`/mall/${mallId}/customers?limit=15&offset=0`);
                        }

                        return defer({
                            customerLists: getCustomerList(mallId, queryParams),
                        });
                    } catch (e) {
                        toast.error('구매자 목록을 불러오는데 실패했습니다.', {
                            toastId: 'customer-data-load-error-toast',
                        });
                        console.error(e);
                        return {
                            list: [],
                            total: 0,
                        };
                    }
                },
            },
            {
                path: 'inquiries',
                element: <LazyRouteElement element={<LazyInquiries />} />,
                loader: async (to) => {
                    try {
                        const { mallId = '' } = to.params;

                        return await getInquiryList(mallId);
                    } catch (e) {
                        toast.error('문의 목록을 불러오는데 실패했습니다.', {
                            toastId: 'inquiry-data-load-error-toast',
                        });
                        console.error(e);
                        return {
                            list: [],
                            total: 0,
                        };
                    }
                },
            },
            {
                path: 'notices',
                element: <LazyRouteElement element={<LazyNotices />} />,
                loader: async (to) => {
                    try {
                        const { mallId = '' } = to.params;

                        return await getNoticeList(mallId);
                    } catch (e) {
                        toast.error('공지사항 목록을 불러오는데 실패했습니다.', {
                            toastId: 'notice-data-load-error-toast',
                        });
                        console.error(e);
                        return {
                            list: [],
                            total: 0,
                        };
                    }
                },
            },
            {
                path: 'statement/sales-list',
                id: 'sales-list',
                element: <LazyRouteElement element={<LazySalesList />} />,
                loader: async (to) => {
                    try {
                        const { mallId = '' } = to.params;
                        const { userId = '-' } = sessionStore.getState();
                        const queryStr = new URL(to.request.url).searchParams.toString();
                        const queryParams = queryStr ? qs.parse(queryStr) : undefined;
                        const check = String(queryParams?.salesType || 'contract') === 'date'; // check가 true면 기간별조회 false면 공구별 조회
                        const contractList = await getSelectableContractList(userId)
                            .then((r) => r)
                            .catch(() => []);
                        if (!!check) {
                            const { list, total } = await getSalesDataByDate(mallId, { ...queryParams } as any);
                            return { list, total, contractList: contractList };
                        } else {
                            const { list, total } = await getSalesDataByContract(mallId, { ...queryParams } as any);
                            return { list, total, contractList: contractList };
                        }
                        // return true
                    } catch (error) {
                        toast.error('목록을 불러오는데 실패했습니다.', { toastId: 'sales-list' });
                        return {
                            list: [],
                            total: 0,
                        };
                    }
                },
            },
            {
                path: 'store-statement/sales-list',
                id: 'store-sales-list',
                element: <LazyRouteElement element={<LazyMallStoreSalesList />} />,
                loader: async () => {
                    // 매출 통계는 클라이언트에서 API 호출
                    return null;
                },
            },
            {
                path: 'statement/calculate-list',
                element: <LazyRouteElement element={<LazyCalculateList />} />,
                loader: async (to) => {
                    return true;
                },
            },
            {
                path: 'statement/vat-report-list',
                element: <LazyRouteElement element={<LazyVATReportList />} />,
                loader: async (to) => {
                    return true;
                },
            },
            {
                path: 'statement/tax-bill-list',
                element: <LazyRouteElement element={<LazyTaxBillList />} />,
                loader: async (to) => {
                    return true;
                },
            },
            {
                path: 'statement/management-sales-deposit',
                element: <LazyRouteElement element={<LazyManagementSalesDeposit />} />,
                loader: async (to) => {
                    return true;
                },
            },
            {
                path: 'recommend-products',
                element: <LazyRouteElement element={<LazyRecommendProducts />} />,
                loader: async (to) => {
                    const { userId = '-' } = sessionStore.getState();
                    const queryStr = new URL(to.request.url).searchParams.toString();
                    const queryParams = queryStr ? qs.parse(queryStr) : undefined;
                    const categoryList = await getCategories()
                        .then((r) => r)
                        .catch(() => []);
                    const { list, total } = await getRecommendProducts(userId, {
                        categoryId: String(queryParams?.categoryId || ''),
                        limit: Number(queryParams?.limit || 50),
                        offset: Number(queryParams?.offset || 0),
                    });
                    return {
                        categoryList,
                        list,
                        total,
                    };
                },
            },
            {
                path: 'seller-chat',
                element: <Chat />,
                loader: async (to) => {
                    try {
                        const mallUserId = await getMallUserIdByToken().catch(() => '');
                        return { mallUserId };
                    } catch (error) {
                        throw error;
                    }
                },
            },
        ],
    },
    {
        path: '/template-demo',
        element: <ManagerLayout />,
        errorElement: <Error />,
        children: [
            {
                path: '',
                // element: <TemplateDemo />
                element: <LazyRouteElement element={<LazyTemplateDemo />} />,
            },
            {
                path: 'toast-editor',
                // element: LazyRouteElement('TemplateDemo/ToastEditor'),
                // element: <ToastEditor/>
                element: <LazyRouteElement element={<LazyToastEditor />} />,
            },
            {
                path: 'dialog',
                // element: LazyRouteElement('TemplateDemo/Dialog'),
                element: <LazyRouteElement element={<LazyDialogPage />} />,
            },
            {
                path: 'button',
                // element: LazyRouteElement('TemplateDemo/Button'),
                element: <LazyRouteElement element={<LazyButtonPage />} />,
            },
            {
                path: 'custom-fields',
                // element: LazyRouteElement('TemplateDemo/Button'),
                element: <LazyRouteElement element={<LazyCustomFieldsPage />} />,
            },
            {
                path: 'drag-and-drop',
                element: <DragAndDrop />,
            },
            {
                path: 'banner-demo',
                element: <LazyRouteElement element={<LazyBannerDemo />} />,
            },
        ],
        loader: () => {
            if (env !== 'dev') {
                console.log('env', env);
                throw {
                    message: 'Page Not Founded',
                    status: 404,
                    statusText: 'test-error-text',
                };
            }

            return true;
        },
    },
    {
        path: '/login',
        element: <Login />,
    },
]);

export default router;

async function linkAppStore() {
    const result = checkMobileOS();
    if (result === 'android') {
        window.location.href = 'https://play.google.com/store/apps/details?id=tpkorea.com.studio&hl=en_US&pli=1';
    }
    if (result === 'ios') {
        window.location.href =
            'https://apps.apple.com/kr/app/%ED%94%8C%EB%A1%9C%EC%9E%89-%EC%8A%A4%ED%8E%98%EC%9D%B4%EC%8A%A4-%EC%8A%A4%ED%8A%9C%EB%94%94%EC%98%A4/id6446928678';
    }
}

/**
 * userStore, spaceUserStore 데이터 동기화
 */
async function syncSessionData() {
    const { userId } = sessionStore.getState();
    if (!userId) return;
    await getUserData(userId);
    await getSpaceUserData(userId);
}

// grade_name에 따라 동적으로 컴포넌트를 렌더링하는 wrapper
function DynamicStudioComponent() {
    const { grade_name } = spaceUserV2Store.getState();

    if (grade_name === '소도몰') {
        return <LazySodomall />;
    }

    return <LazyEditProfileLink />;
}
